/**
 * たたんだ所（`<details>`）と差し込む入力欄の、開閉の動き（UI-MOTION・`docs/10` §4）。
 *
 * ★`document` の click を1つで受ける。JS で後から作る `<details>` にも自動で当たる（個別に配線しない）。
 * ★動かせない環境（`prefers-reduced-motion: reduce`・`element.animate` が無い端末）では**何もしない**。
 *   `preventDefault` しないので、標準の開閉がそのまま働く。
 * ★`open` の真偽は今までどおり変わる。`toggle` イベント（`foldCard` の記憶・遅延読み込み）は出る。
 */

/** ★ここと `styles.css` の `--ease-open` / `--ease-close` は同じ値。片方を変えたらもう片方も（検査が見張る） */
export const EASE_OPEN = "cubic-bezier(0.23, 1, 0.32, 1)";
export const EASE_CLOSE = "cubic-bezier(0.55, 0, 1, 0.45)";

type Timing = { open: number; close: number };
export const TIMING = {
  /** 設定タブのカードなど */
  fold: { open: 240, close: 180 },
  /** 一覧の中の畳み。続けて開け閉めするので短い */
  list: { open: 180, close: 140 },
  /** 右上のアカウント。高さは動かさない */
  account: { open: 150, close: 120 },
} as const satisfies Record<string, Timing>;

/** 中身が高さに遅れて薄く出る、その遅れ（ms） */
const CONTENT_DELAY = 60;
/** アカウントが上から降りてくる距離（px） */
const SLIDE_PX = 4;

/** 動かしてよいか。★判定を関数に切り出してあるのは、検査が環境を差し替えられるようにするため */
export function motionEnabled(): boolean {
  if (typeof Element.prototype.animate !== "function") return false;
  return !window.matchMedia("(prefers-reduced-motion: reduce)").matches;
}

type Kind = "fold" | "list" | "account";

export function kindOf(details: HTMLDetailsElement): Kind {
  if (details.classList.contains("account")) return "account";
  return details.matches("ul.list li details") ? "list" : "fold";
}

/** 動いている間の状態。途中で押されたら、これを止めて逆向きに作り直す */
type Run = { opening: boolean; settle: () => void; abort: () => void };
const runs = new Map<HTMLDetailsElement, Run>();

const px = (n: number): string => `${n}px`;

/** 畳んだときの高さ。`open` を触らずに（`toggle` を余計に出さずに）見出しの下端から求める */
function collapsedHeight(details: HTMLDetailsElement, summary: HTMLElement): number {
  const cs = getComputedStyle(details);
  return (
    summary.getBoundingClientRect().bottom -
    details.getBoundingClientRect().top +
    parseFloat(cs.paddingBottom || "0") +
    parseFloat(cs.borderBottomWidth || "0")
  );
}

/** inline の style を使い終わったら外す（空の `style=""` も残さない） */
function clearInline(node: HTMLElement, ...props: string[]): void {
  for (const p of props) node.style.removeProperty(p);
  if (node.getAttribute("style") === "") node.removeAttribute("style");
}

function contentOf(details: HTMLDetailsElement, summary: HTMLElement): HTMLElement[] {
  return [...details.children].filter((c): c is HTMLElement => c !== summary && c instanceof HTMLElement);
}

function currentOpacity(node: HTMLElement): number {
  const o = parseFloat(getComputedStyle(node).opacity);
  return Number.isNaN(o) ? 1 : o;
}

/** `<summary>` が押されたとき。動かすなら標準の開閉を止めて、こちらで開閉する */
function onSummaryClick(e: MouseEvent): void {
  if (e.defaultPrevented || !(e.target instanceof Element)) return;
  const summary = e.target.closest("summary");
  const details = summary?.parentElement;
  if (!(details instanceof HTMLDetailsElement) || summary === null || details.querySelector("summary") !== summary) return;
  // ★キーボード（Enter／Space）が起こした click は `detail` が 0。動かさず、標準の開閉に任せる（docs/10 §4）。
  //   開く印の回転も一瞬にする：次の描画が済むまで `data-instant`（styles.css が transition を切る）
  if (e.detail === 0) {
    details.setAttribute("data-instant", "");
    requestAnimationFrame(() => requestAnimationFrame(() => details.removeAttribute("data-instant")));
    return;
  }
  if (!motionEnabled()) return;
  e.preventDefault();
  toggleDetails(details, summary);
}

export function toggleDetails(details: HTMLDetailsElement, summary: HTMLElement): void {
  // 描き直しで画面から外れた details の動きを持ち続けない
  for (const [d, r] of runs) {
    if (!d.isConnected) r.abort();
  }
  const prev = runs.get(details);
  const opening = prev === undefined ? !details.open : !prev.opening;
  const kind = kindOf(details);
  const t = TIMING[kind];
  const ms = opening ? t.open : t.close;
  const easing = opening ? EASE_OPEN : EASE_CLOSE;
  const content = contentOf(details, summary);

  // ★測るのは止める前。止めると見た目が元の高さ・濃さへ戻る
  const fromHeight = details.getBoundingClientRect().height;
  const fromOpacity = Math.min(...content.map(currentOpacity), 1);
  prev?.abort();

  const anims: Animation[] = [];
  let cleanup = (): void => {};
  if (kind === "account") {
    // 高さは動かさない。中身だけが薄く出て（開く）、薄くなる（閉じる）
    if (opening) details.open = true;
    const from = opening && prev === undefined ? 0 : fromOpacity;
    const to = opening ? 1 : 0;
    for (const c of content) {
      const y = opening ? -SLIDE_PX * (1 - from) : 0;
      anims.push(
        c.animate(
          [
            { opacity: from, transform: `translateY(${px(y)})` },
            { opacity: to, transform: `translateY(${px(0)})` },
          ],
          { duration: ms, easing, fill: "forwards" },
        ),
      );
    }
  } else {
    let toHeight: number;
    if (opening) {
      details.open = true;
      toHeight = details.getBoundingClientRect().height;
    } else {
      toHeight = collapsedHeight(details, summary);
    }
    details.style.overflow = "hidden";
    cleanup = () => clearInline(details, "overflow");
    anims.push(
      details.animate([{ height: px(fromHeight) }, { height: px(toHeight) }], { duration: ms, easing, fill: "forwards" }),
    );
    if (opening) {
      for (const c of content) {
        anims.push(
          c.animate([{ opacity: prev === undefined ? 0 : fromOpacity }, { opacity: 1 }], { duration: ms - CONTENT_DELAY, delay: CONTENT_DELAY, easing, fill: "backwards" }),
        );
      }
    }
  }

  if (anims.length === 0) {
    details.open = opening;
    return;
  }

  // ★閉じる動きの間は `open` が残る。開く印（▸）の回転を戻す合図は `open` ではなくこの印で出す（styles.css）
  if (!opening) details.setAttribute("data-closing", "");

  let ended = false;
  const end = (apply: boolean): void => {
    if (ended) return;
    ended = true;
    details.removeAttribute("data-closing");
    for (const a of anims) {
      a.onfinish = null;
      a.cancel();
    }
    cleanup();
    runs.delete(details);
    if (apply && !opening) details.open = false;
  };
  const run: Run = { opening, settle: () => end(true), abort: () => end(false) };
  runs.set(details, run);
  anims[0]!.onfinish = run.settle;
}

/** 印刷の前に、動いている開閉を終わらせる（途中の高さで固まった畳みが印刷に出ないように） */
export function settleAll(): void {
  for (const run of [...runs.values()]) run.settle();
}

/**
 * 行の下に差し込む入力欄（時給）の動き。★差し込んだ**後**に呼ぶ。高さは 0 から自分の高さまで。
 * フォーカスは待たずに呼び出し側が移す（動きは目で追うためで、入力を待たせない）。
 */
export function revealBlock(node: HTMLElement): void {
  if (!motionEnabled()) return;
  const h = node.getBoundingClientRect().height;
  node.style.overflow = "hidden";
  const a = node.animate([{ height: "0px", opacity: 0 }, { height: px(h), opacity: 1 }], {
    duration: TIMING.fold.open,
    easing: EASE_OPEN,
  });
  a.onfinish = () => clearInline(node, "overflow");
}

/** 取り外す前の動き。★縮み終わってから `done`（`remove`）を呼ぶ。動かせないときはすぐ呼ぶ */
export function concealBlock(node: HTMLElement, done: () => void): void {
  if (!motionEnabled()) {
    done();
    return;
  }
  const h = node.getBoundingClientRect().height;
  // 差し込みの動きの途中で取り外されたら、そちらを止める（終わりの後始末が縮みの途中で走らないように）
  for (const old of node.getAnimations?.() ?? []) {
    old.onfinish = null;
    old.cancel();
  }
  node.style.overflow = "hidden";
  const a = node.animate([{ height: px(h), opacity: 1 }, { height: "0px", opacity: 0 }], {
    duration: TIMING.fold.close,
    easing: EASE_CLOSE,
    fill: "forwards",
  });
  a.onfinish = done;
}

/** 起動時に1回。`main.ts` が呼ぶ */
export function initFoldMotion(): void {
  document.addEventListener("click", onSummaryClick);
  window.addEventListener("beforeprint", settleAll);
}
